import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import useAddressesManager from "../../hooks/useAddressesManager";
import Alert from "../../components/alert/Alert";
import { displayDate } from "../../utils/date";
import Button from "../../components/ui/button";
import Th from "../../components/ui/table/Th";
import Tr from "../../components/ui/table/Tr";
import Td from "../../components/ui/table/Td";
import Tbody from "../../components/ui/table/Tbody";
import { IconProps } from "../../types/icon";
import Paginate from "../../components/ui/pagination/Paginate";
import DisplayItems from "../../components/ui/pagination/DisplayItems";
import { Helmet } from "react-helmet-async";
import PageSectionHeader from "../../components/PageSectionHeader";


const BACKEND_URL = import.meta.env.VITE_API_URI;

const DonwloadIcon = ({className}: IconProps) => {
  return (
    <svg className={className} width="800px" height="800px" viewBox="0 0 24 24" fill="none">
      <path id="Vector" d="M6 21H18M12 3V17M12 17L17 12M12 17L7 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

const ManageAddresses = () => {
  const intl = useIntl();
  const {addressesList, getAllAddresses, generateUrl, generationLoading, error, loading}  = useAddressesManager();
  const [numberToGenerate, setNumber] = useState(10);


  useEffect(() => {
    getAllAddresses({page: 1});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onGenerate = () => {
    generateUrl(numberToGenerate);
  }

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "addresses.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>
      <PageSectionHeader className="mb-0">
        <h1 className="py-2 text-xl font-semibold text-[#023865]">
          {intl.formatMessage({ id: "addresses.heading" })}
        </h1>
      </PageSectionHeader>
      
      { error ?
        <Alert className="my-5 w-fit" type="error" message={error.message} /> : ''
      }

      <label htmlFor="number" className="mb-1.5 mt-5 block text-shadow text-sm">
        {intl.formatMessage({ id: "addresses.countLabel" })}
      </label>
      <div className="mb-5 w-fit flex flex-col sm:flex-row gap-2 sm:items-center">
        <input id="number"
          type="number"
          className="py-3 my-0 w-fit input"
          required value={numberToGenerate}
          onChange={e => setNumber(+e.target.value)}/>
        <Button
          width="fit"
          className="px-4 !bg-[#E94E1B] hover:!bg-[#d34515] focus-visible:!ring-[#E94E1B]"
          theme="primary"
          disabled={generationLoading ?? false}
          onClick={onGenerate}>
          {intl.formatMessage({ id: "addresses.generate" })}
        </Button>
        { generationLoading ?
          <span className="text-accent text-sm font-semibold">
            {intl.formatMessage({ id: "addresses.generating" })}
          </span> : ''
        }
      </div>

      { addressesList && addressesList.data.length > 0 ? (
        <>
        <DisplayItems itemLabel={intl.formatMessage({ id: "addresses.filesLabel" })} {...addressesList} />
        <div className="py-2 px-1 overflow-x-auto rounded-lg border border-[#D8E3EF]">
          <table className="w-full">
            <thead>
              <tr className="capitalize bg-[#023865] [&>th]:!text-white">
                <Th>{intl.formatMessage({ id: "addresses.colId" })}</Th>
                <Th>{intl.formatMessage({ id: "addresses.colFileName" })}</Th>
                <Th>{intl.formatMessage({ id: "addresses.colTotal" })}</Th>
                <Th>{intl.formatMessage({ id: "addresses.colGeneratedAt" })}</Th>
                <Th width="50px">{intl.formatMessage({ id: "addresses.colCsv" })}</Th>
              </tr>
            </thead>
            <Tbody>
              {addressesList.data.map((address, i) => {
                const fileurl = address.file ? BACKEND_URL + address.file : '';
                return (
                  <Tr key={address.id} isFirstRow={i===0}>
                    <Td>{address.id}</Td>
                    <Td>{address.fileName}</Td>
                    <Td>{address.totalUrl}</Td>
                    <Td>{displayDate(address.groupCreatedAt)}</Td>
                    <Td isLastCell={true}>
                      {fileurl.length > 0 ?
                        <a className="text-[#023865] hover:text-[#E94E1B]"
                          href={fileurl} target="_blank">
                          <DonwloadIcon className="size-5"/>
                        </a> : ''
                      }
                    </Td>
                  </Tr>
                )})
              }
            </Tbody>
          </table>
        </div>
        {/* Pagination */}
        { addressesList.totalPages > 1 &&
          <div className="my-5 ml-auto w-fit">
            <Paginate {...addressesList} to={page => getAllAddresses({page})}/>
          </div>
        }
        </>
        ) : !loading &&
        <p className="text-sh text-4xl font-semibold fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {intl.formatMessage({ id: "addresses.empty" })}
        </p>
      }
      
      {
        loading && (
          <p className="my-5 text-center text-xs">
            {intl.formatMessage({ id: "common.loading" })}
          </p>
        )
      }
    </>
  )
}

export default ManageAddresses;
